import classNames from "classnames";
import { Modal } from "common/Modal";
import { Table } from "common/Table/Table";
import { ToggleButton } from "common/form/ToggleButton";
import { useNotifications } from "hooks/useNotification";
import {
  ChangeEvent,
  Fragment,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import UserService from "services/user.service";
import swal from "sweetalert";
import { exportToCSV, formatDate } from "utils/funcs";
import { tabStyles } from "./GeneralData";
import { TNTWinners } from "components/TNTWinners";

const service = new UserService();

export const Giveaway = () => {
  const [previewImage, setPreviewImage] = useState("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const columns = [
    {
      key: "category_image",
      title: "Image",
      label: "Image",
      dbColName: "image",
      render: (image: string) => {
        return (
          <div className="h-14 w-44 rounded-lg relative">
            <img
              src={image}
              className="w-full h-full object-cover rounded-lg"
              alt="category_image"
            />
            <button
              type="button"
              onClick={() => {
                setPreviewImage(image);
                setShowPreviewModal(true);
              }}
              className="group absolute w-full transition-colors ease-in-out flex items-center justify-center  top-0 h-full hover:bg-zinc-300 hover:bg-opacity-50 rounded-lg text-white"
            >
              <span className="hidden group-hover:block delay-100">View</span>
            </button>
          </div>
        );
      },
    },
    {
      key: "type",
      title: "type",
      label: "type",
      render: t => t === "current_week" ? "Current Week" : "Last Week",
      dbColName: "type",
    },
    {
      key: "location",
      title: "Location",
      label: "Location",
      dbColName: "location",
    },
    {
      key: "winner",
      title: "Winner",
      label: "Winner",
      dbColName: "winner",
    },
    {
      key: "createdAt",
      title: "Date created",
      label: "Date created",
      dbColName: "createdAt",
      render: (date: string) => formatDate(date),
    },
    {
      key: "status",
      title: "Status",
      label: "Status",
      render: ({ _id, is_active }) => (
        <ToggleButton
          value={is_active}
          id={`status-${_id}`}
          onChange={(e) => changeStatus(_id, e)}
        />
      ),
    },
    {
      key: "actions",
      title: "actions",
      render: (item: any) => {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEditClick(item)}
              className="bg-blue-500 text-white rounded-md text-xs p-2 flex items-center gap-1"
              title="Edit"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                />
              </svg>
              Edit
            </button>
            <button
              onClick={() => handleDelete(item._id)}
              className="bg-primary text-white rounded-md text-xs p-2 "
            >
              Delete
            </button>
          </div>
        );
      },
    },
  ];

  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [showImage, setShowImage] = useState<string | null>();
  const [image, setImage] = useState<any>();
  const [type, setType] = useState("");
  const [location, setLocation] = useState("");
  const [winner, setWinner] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editWinner, setEditWinner] = useState("");
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [tntWinners, setTNTWinners] = useState<any>([]);
  const [tab, setTab] = useState("Giveaway Images");
  const [showTnTModal, setShowTnTModal] = useState(false);
  const [galleryData, setGalleryData] = useState<any[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  const { errorMessage, successMessage } = useNotifications();
  const fetchData = useCallback(() => {
    setLoading(true);
    service
      .getGiveaway()
      .then((resp) => {
        setData(resp.data.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });


  }, []);

  const fetchGallery = useCallback(() => {
    setGalleryLoading(true);
    service
      .getGiveawayGallery()
      .then((resp) => {
        setGalleryData(resp.data.data ?? []);
      })
      .catch((error) => {
        console.log(error);
        errorMessage("Could not load giveaway photo archive");
      })
      .finally(() => {
        setGalleryLoading(false);
      });
  }, [errorMessage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (tab === "Giveaway Photos") {
      fetchGallery();
    }
  }, [tab, fetchGallery]);

  const checkStatus = () => {
    // Counters for 'current_week' and 'previous_week'
    let currentWeekCount = 0;
    let previousWeekCount = 0;

    // Iterate through the array
    for (const obj of data) {
      if (obj.type === "current_week" && obj.is_active) {
        currentWeekCount++;
      } else if (obj.type === "last_week" && obj.is_active) {
        previousWeekCount++;
      }
    }

    // Check if both conditions are met
    return currentWeekCount >= 1 && previousWeekCount >= 1;
  };

  const handleDelete = (id: string) => {
    swal({
      title: "Are you sure, you want to delete this image?",
      icon: "warning",
      className: "w-[500px]",
      buttons: {
        cancel: {
          text: "Cancel",
          visible: true,
        },
        confirm: {
          text: "Delete",
          className: "bg-primary text-white",
        },
      },
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setLoading(true);
        service.deleteImage(id).then((resp) => {
          if (resp.data.success) {
            successMessage("Image deleted successfully");
            const shallowCopy = [...data];
            const newData = shallowCopy.filter((img) => img._id !== id);
            setData(newData);
            setLoading(false);
          }
        });
      }
    });
  };

  const changeStatus = (id: string, e) => {
    const shallowCopy = [...data]
    const found = shallowCopy.find(item => item._id === id)
    found.is_active = e.target.checked
    setData(shallowCopy)
    service.changeImageStatus(id, { is_active: e.target.checked }).then((resp) => {
      successMessage("Status updated successfully");
    }).catch((err) => {
      console.error(err)
    })
  };

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    if (!file) return;

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const fileType = file.type.toLowerCase();

    if (!allowedTypes.includes(fileType)) {
      errorMessage("Please upload only JPEG, PNG, or JPG images");
      event.target.value = ''; // Reset input
      return;
    }

    const reader = new FileReader();
    setImage(file);

    reader.onload = () => {
      if (reader.readyState === 2) {
        setShowImage(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleEditClick = (item: any) => {
    setEditingItem(item);
    setEditLocation(item.location || "");
    setEditWinner(item.winner || "");
    setShowEditModal(true);
  };

  const handleEditSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    if (!editLocation) {
      errorMessage("Please enter location");
      return;
    }

    if (!editWinner) {
      errorMessage("Please enter winner");
      return;
    }

    if (!editingItem) return;

    setUpdating(true);

    service
      .changeImageStatus(editingItem._id, {
        location: editLocation,
        winner: editWinner,
      })
      .then((resp) => {
        if (resp.data.success) {
          successMessage("Giveaway updated successfully");
          // Update the item in local state
          const updatedData = data.map((item: any) =>
            item._id === editingItem._id
              ? { ...item, location: editLocation, winner: editWinner }
              : item
          );
          setData(updatedData);
          fetchGallery();
          setShowEditModal(false);
          setEditingItem(null);
          setEditLocation("");
          setEditWinner("");
        }
      })
      .catch((error) => {
        console.error(error);
        errorMessage("Failed to update giveaway");
      })
      .finally(() => {
        setUpdating(false);
      });
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    if (!type) {
      errorMessage("Please select type");
      return;
    }

    if (!image) {
      errorMessage("Please select image");
      return;
    }

    if (!location) {
      errorMessage("Please enter location");
      return;
    }

    if (!winner) {
      errorMessage("Please enter winner");
      return;
    }

    setSaving(true);

    service
      .addImages({ type, is_active: false, image, location, winner })
      .then((resp) => {
        fetchData();
        fetchGallery();
        setImage("");
        setShowImage("");
        setLocation("");
        setWinner("");
        successMessage("Image added successfully");
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setSaving(false);
        setShowModal(false);
      });
  };

  const shouldBeDisabled = checkStatus();

  const handleExport = () => {
    exportToCSV(data, "giveaway");
  };

  return (
    <Fragment>
      <div className="flex justify-between items-center mb-4">

        <div>
          <h1 className="page-title">
            {tab}
          </h1>
          <p className="page-subtitle">
            {tab === "Giveaway Photos"
              ? "Archive of every upload — photos stay here after a giveaway row is deleted"
              : "View all "}
          </p>
        </div>

        {tab !== "Giveaway Photos" && (
          <button
            type="button"
            onClick={() => {
              if (tab === "Giveaway Images") {
                setShowModal(true)
              } else {
                setShowTnTModal(true)
              }
            }}
            disabled={shouldBeDisabled}
            className="bg-green-500 disabled:cursor-not-allowed disabled:bg-green-300 text-white rounded-md p-2.5 text-sm flex items-center"
          >
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v12m6-6H6"
                />
              </svg>
            </span>
            Add
          </button>
        )}
      </div>

      <div className="tabs mb-4">

        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setTab("Giveaway Images")}
            className={classNames(tabStyles.default, {
              [tabStyles.active]: tab === "Giveaway Images",
            })}
          >
            Giveaway Images
          </button>
          <button
            type="button"
            onClick={() => setTab("TNT Winners")}
            className={classNames(tabStyles.default, {
              [tabStyles.active]: tab === "TNT Winners",
            })}
          >
            TNT Winners
          </button>
          <button
            type="button"
            onClick={() => setTab("Giveaway Photos")}
            className={classNames(tabStyles.default, {
              [tabStyles.active]: tab === "Giveaway Photos",
            })}
          >
            Giveaway Photos
          </button>
        </div>

      </div>

      {tab === "Giveaway Images" && (
        <Table
          data={data}
          loadingData={loading}
          handleExport={handleExport}
          exportButton={false}
          totalItems={data.length}
          columns={columns}
        />
      )}

      {tab === "TNT Winners" && (
        <TNTWinners showModal={showTnTModal} setShowModal={setShowTnTModal} />
      )}

      {tab === "Giveaway Photos" && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          {galleryLoading ? (
            <p className="text-center text-sm text-gray-500 py-16">Loading images…</p>
          ) : galleryData.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-16">
              No archived giveaway photos yet. Add images from the Giveaway Images tab — they are
              kept here even if you delete the row later.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {galleryData
                .filter((item: any) => Boolean(item?.image))
                .map((item: any) => {
                  const src = item.image as string;
                  return (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => {
                        setPreviewImage(src);
                        setShowPreviewModal(true);
                      }}
                      className="group relative aspect-[4/3] w-full overflow-hidden rounded-lg border border-gray-100 bg-gray-50 text-left shadow-sm transition hover:ring-2 hover:ring-green-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <img
                        src={src}
                        alt={item.location ? `Giveaway — ${item.location}` : "Giveaway"}
                        className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                        loading="lazy"
                      />
                      <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-2 pt-8 text-xs text-white opacity-0 transition group-hover:opacity-100">
                        {[item.type === "current_week" ? "Current Week" : item.type === "last_week" ? "Last Week" : item.type, item.location]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      )}

      <Modal
        width="w-full md:w-1/3"
        showModal={showModal}
        setShowModal={setShowModal}
        title="Add New Giveaway"
        onClose={() => {
          setImage("");
          setShowImage(null);
          setType("");
          setLocation("");
          setWinner("");
          // Reset file input
          const fileInput = document.getElementById('dropzone-file') as HTMLInputElement;
          if (fileInput) fileInput.value = '';
        }}
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <select
            onChange={(e) => setType(e.target.value)}
            value={type}
            className="w-full px-3 py-2 text-sm"
          >
            <option value="">Please select type</option>
            <option value="current_week">Current Week</option>
            <option value="last_week">Last Week</option>
          </select>
          <div className="flex flex-col gap-1">
            <label className="field-label">Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Enter location"
              className="w-full px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="field-label">Winner</label>
            <input
              type="text"
              value={winner}
              onChange={(e) => setWinner(e.target.value)}
              placeholder="Enter winner name"
              className="w-full px-3 py-2 text-sm"
            />
          </div>
          {showImage ? (
            <div className="relative h-full">
              <img
                alt="category"
                className="w-full h-full overflow-hidden object-contain rounded-lg"
                src={showImage}
              />
              <button
                type="button"
                onClick={() => {
                  setShowImage(null);
                  setImage(null);
                  // Reset file input
                  const fileInput = document.getElementById('dropzone-file') as HTMLInputElement;
                  if (fileInput) fileInput.value = '';
                }}
                className="bg-gray-300 h-10 w-10 rounded-full absolute top-3 right-3"
              >
                X
              </button>
            </div>
          ) : (
            <div className="z-20 flex items-center justify-center w-full">
              <label
                htmlFor="dropzone-file"
                className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg
                    className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 16"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                    />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">Click to upload</span> or
                    drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    JPEG, PNG, JPG only (MAX. 800x400px)
                  </p>
                </div>
                <input
                  id="dropzone-file"
                  onChange={handleUpload}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  className="hidden"
                />
              </label>
            </div>
          )}
          <button
            type="submit"
            disabled={saving}
            className="bg-green-400 disabled:cursor-not-allowed rounded-md font-medium text-sm disabled:bg-green-300 text-white p-2.5"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </form>
      </Modal>

      <Modal
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        title="Edit Giveaway"
        width="w-full md:w-1/3"
        onClose={() => {
          setEditingItem(null);
          setEditLocation("");
          setEditWinner("");
        }}
      >
        <form onSubmit={handleEditSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="field-label">Location</label>
            <input
              type="text"
              value={editLocation}
              onChange={(e) => setEditLocation(e.target.value)}
              placeholder="Enter location"
              className="w-full px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="field-label">Winner</label>
            <input
              type="text"
              value={editWinner}
              onChange={(e) => setEditWinner(e.target.value)}
              placeholder="Enter winner name"
              className="w-full px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowEditModal(false);
                setEditingItem(null);
                setEditLocation("");
                setEditWinner("");
              }}
              className="bg-gray-300 text-gray-700 rounded-md font-medium text-sm px-4 py-2.5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updating}
              className="bg-blue-500 disabled:cursor-not-allowed rounded-md font-medium text-sm disabled:bg-blue-300 text-white px-4 py-2.5"
            >
              {updating ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        showModal={showPreviewModal}
        setShowModal={setShowPreviewModal}
        title="Image Preview"
        width="w-1/3"
      >
        <div className="h-full w-full">
          <img src={previewImage} alt="receipt" className="h-full w-full" />
        </div>
      </Modal>
    </Fragment>
  );
};
